/**
 * "Have the suppliers come back yet?" for the Requirements board — pure derivation, no I/O.
 *
 * ---- Why two signals, not one ----
 *
 * `RFQ.status` and "a supplier actually wrote back" are DIFFERENT facts, and neither contains the
 * other. Measured on the live bench 2026-08-08:
 *
 * | RFQ            | status    | received mail linked to it | Supplier Quotation |
 * |----------------|-----------|----------------------------|--------------------|
 * | RFQ-2026-00011 | `Sent`    | YES (shibyavu@gmail.com)   | none               |
 * | RFQ-2026-00002 | `Replied` | none                       | five               |
 *
 * The server sets `Replied` when a `Supplier Quotation` exists against the RFQ — NOT when a human
 * answers. So trusting `status` alone would show 0/1 on RFQ-2026-00011, whose supplier had already
 * replied, and offer to chase them: the exact "remind someone who already answered" failure the
 * reply plumbing was built to end. Trusting mail alone would miss RFQ-2026-00002, which demonstrably
 * came back. A supplier has answered if EITHER says so — including a "sorry, no stock" decline,
 * which answers the board's question ("who still owes me a reply?") just as well as a quotation.
 *
 * The quotation/mail-only distinction is not thrown away: it rides on `SupplierReplyState.quoted`
 * for the card's tooltip, so "came back" and "actually priced it" stay separable where it matters.
 *
 * ---- Why `Cancelled` and `Expired` are dropped ----
 *
 * `RFQ.status`'s full vocabulary, read off the DocType itself, is
 * `Draft | Sent | Replied | Expired | Cancelled`. `toRfqStatus` collapses everything that is not
 * a reply to `'sent'`, so a cancelled RFQ would otherwise be counted as a supplier who still owes
 * an answer. Neither state exists on the bench today, so this branch is justified by the schema
 * rather than by a live row — it is covered by the tests below, not by measurement.
 */

import { daysAwaiting } from './rfq'
import { toRfqStatus } from './api/useEnquiries'

/** The columns where "who hasn't come back?" is an actionable question. */
export const WATCHED_STATUSES = ['RFQ Sent', 'Quotes Received', 'Comparison Done'] as const

/** Days of silence past which the chip stops being grey. Matches `RemindModal`'s own threshold. */
export const OVERDUE_AFTER_DAYS = 3

/** Statuses that take an RFQ out of the reckoning entirely — nobody is waiting on these. */
const RETIRED_STATUSES = new Set(['cancelled', 'expired'])

/** The subset of an `RFQ` row this derivation needs. */
export interface BoardRfq {
  name: string
  enquiry: string
  /** Raw wire value, matched case-insensitively — never a pre-collapsed two-value status. */
  status: string
  /** Display name, already trimmed of the wire's leading space (`wire.ts`'s `trimmed()`). */
  supplier_name: string
  sent_at: string | null
}

export interface SupplierReplyState {
  supplier: string
  rfq: string
  replied: boolean
  /** A `Supplier Quotation` exists, as opposed to a reply we have only seen as mail. */
  quoted: boolean
  /** Days since this RFQ went out, or `null` when it has no send time. */
  waitingDays: number | null
}

export interface EnquiryReplyState {
  /** Denominator: RFQs that went out, or came back anyway. Never counts an unsent Draft. */
  total: number
  replied: number
  /** The LONGEST silence among those still outstanding — the one worth chasing. */
  waitingDays: number | null
  /** Per supplier, newest-silence first, for the card's tooltip. */
  suppliers: SupplierReplyState[]
}

/**
 * Group RFQs by enquiry and decide, for each, how many suppliers have come back.
 *
 * `repliedRfqNames` is the set of RFQ docnames with at least one RECEIVED `Communication` linked
 * to them — our own outgoing RFQ and reminder mails link to the same RFQ, so the caller must
 * filter on `sent_or_received` before building this set or every RFQ reads as answered by its own
 * send.
 */
export function deriveReplyStates(
  rfqs: readonly BoardRfq[],
  repliedRfqNames: ReadonlySet<string>,
  now: Date,
  /**
   * RFQs with a `Supplier Quotation` recorded against them — the THIRD signal, and the only one
   * that is the fact itself rather than an inference from it.
   *
   * Added 2026-08-13: a card read `0/1 replied` for an enquiry whose supplier had replied and
   * whose quotation had been recorded. Both older signals missed it — the RFQ's status was never
   * moved to `Replied` (we create the quotation; the server does not restatus the RFQ), and the
   * reply came in as a forward with no `reference_name`, so no mail was linked (`D12`).
   *
   * Optional so every existing caller and test keeps working unchanged; absent behaves exactly as
   * before.
   */
  quotedRfqNames: ReadonlySet<string> = new Set(),
): Map<string, EnquiryReplyState> {
  const byEnquiry = new Map<string, SupplierReplyState[]>()

  for (const rfq of rfqs) {
    if (RETIRED_STATUSES.has(rfq.status.trim().toLowerCase())) continue

    // Either the server restatused the RFQ, or a quotation exists against it. The second is
    // authoritative and the first is a convenience — a quotation cannot exist without a reply.
    const quoted = toRfqStatus(rfq.status) === 'received' || quotedRfqNames.has(rfq.name)
    const replied = quoted || repliedRfqNames.has(rfq.name)

    // An RFQ nobody mailed is not a supplier who is late — unless it somehow answered, in which
    // case the reply is the proof it went out and the missing `sent_at` is the unreliable field.
    if (rfq.sent_at === null && !replied) continue

    const list = byEnquiry.get(rfq.enquiry) ?? []
    list.push({
      supplier: rfq.supplier_name,
      rfq: rfq.name,
      replied,
      quoted,
      waitingDays: daysAwaiting(rfq.sent_at, now),
    })
    byEnquiry.set(rfq.enquiry, list)
  }

  const out = new Map<string, EnquiryReplyState>()
  for (const [enquiry, suppliers] of byEnquiry) {
    const outstanding = suppliers.filter((s) => !s.replied)
    const waits = outstanding.map((s) => s.waitingDays).filter((d): d is number => d !== null)
    out.set(enquiry, {
      total: suppliers.length,
      replied: suppliers.filter((s) => s.replied).length,
      waitingDays: waits.length > 0 ? Math.max(...waits) : null,
      // Longest-silent first: the tooltip's first line should be whoever most needs chasing.
      suppliers: [...suppliers].sort((a, b) => (b.waitingDays ?? -1) - (a.waitingDays ?? -1)),
    })
  }
  return out
}

/** The chip's own label. `total` is never 0 here — the card renders nothing in that case. */
export function replyChipLabel(state: EnquiryReplyState): string {
  return state.replied === state.total
    ? `✓ all ${state.total} replied`
    : `🗨 ${state.replied}/${state.total} replied`
}

/**
 * Grey while the wait is normal, amber once it has earned it, green when everyone is in.
 *
 * Driven by the RFQ's own `sent_at`, NOT the enquiry's `age_days`: the question is how long the
 * suppliers have been silent, not how long the enquiry has sat in its column — an enquiry can
 * change status for reasons that have nothing to do with a supplier.
 */
export function replyChipTone(state: EnquiryReplyState): 'ok' | 'warn' | 'muted' {
  if (state.replied === state.total) return 'ok'
  if (state.waitingDays !== null && state.waitingDays >= OVERDUE_AFTER_DAYS) return 'warn'
  return 'muted'
}

/** Per-supplier detail for the chip's tooltip — where "replied" and "quoted" stay distinguishable. */
export function replyChipTooltip(state: EnquiryReplyState): string {
  return state.suppliers
    .map((s) => {
      const who = s.supplier === '' ? s.rfq : s.supplier
      if (!s.replied) {
        if (s.waitingDays === null) return `${who} — awaiting`
        if (s.waitingDays === 0) return `${who} — awaiting, sent today`
        return `${who} — awaiting ${s.waitingDays} day${s.waitingDays === 1 ? '' : 's'}`
      }
      return `${who} — replied${s.quoted ? ' (quotation received)' : ' (mail only)'}`
    })
    .join('\n')
}
